"use client"
import { useEffect, useState } from "react"

/**
 * Hook to determine if the current device is mobile based on the user agent and window size.
 *
 * @returns Boolean indicating if the current device is a mobile device.
 *
 * This hook checks the browser's user agent string and the window size to identify
 * mobile devices. It's useful for rendering conditionally or adjusting UI based
 * on device type.
 *
 * Usage example:
 *
 * const isMobileDevice = useIsMobile();
 *
 * if (isMobileDevice) {
 *   // Adjust layout or functionality for mobile devices
 * }
 */

export const useIsMobile = (width?: number): boolean => {
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const checkMobile = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera
            const mobile = Boolean(
                userAgent.match(
                    /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
                ) || window.innerWidth <= (width ?? 768)
            )
            setIsMobile(mobile)
        }

        checkMobile()

        window.addEventListener("resize", checkMobile)

        return () => {
            window.removeEventListener("resize", checkMobile)
        }
    }, [])

    return isMobile
}
